<template>
  <fieldset class="p-fieldset">
    <legend class="p-fieldset-legend p-p-2">{{ $t('Address') }}</legend>
    <div v-if="userAddressIsNotEmpty">
      <div v-if="userData.address" class="p-m-2">
        {{ addressLine(userData.address) }}
      </div>
      <div v-if="userData.address?.comment"
           class="p-m-2 address-comment">
        {{ $t('Comment') }}: {{ userData.address.comment }}
      </div>

      <div>
        <Button style="width: auto"
                :loading="userAddressInFlight"
                :disabled="userAddressInFlight"
                :label="$t('Edit')"
                icon="ti-pencil"
                class="p-button-text"
                @click="editAddress(userData.address)"/>
        <Button style="width: auto"
                :loading="userAddressInFlight"
                :disabled="userAddressInFlight"
                :label="$t('Delete')"
                icon="ti-trash"
                class="p-button-text"
                @click="deleteAddressOnClick"/>
      </div>
    </div>
    <div v-else class="p-d-flex">
      <Button style="width: auto"
              :loading="userAddressInFlight"
              :disabled="userAddressInFlight"
              :label="$t('Add')"
              icon="pi pi-plus"
              class="p-button-text"
              @click="addAddress"/>
    </div>
  </fieldset>

  <AddressModal :visible="addressModal"
                @close="closeAddressModal"
                :userId="userData.id ? +userData.id : null"
                :item="userData.address"
                @update-item="updateItem"
                @update-items="updateItems"/>

  <ConfirmModal :visible="confirmDeleteAddressModal"
                :modalDataIsLoading="modalDataIsLoading"
                @close="closeConfirmDeleteAddressModal"
                @confirm="deleteAddress"/>
</template>

<script>
import AddressModal from "@/pages/users/components/AddressModal";
import httpClient from "@/services/http.services";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import labelMixin from "@/mixins/labelMixin";

export default {
  emits: ['update-items', 'update-item'],
  mixins: [ showErrorsMixins, labelMixin ],
  components: { AddressModal },
  name: 'AddressFieldset',
  props: {
    user: {
      type: Object,
      default: null
    },
    userAddressInFlight: {
      type: Boolean,
      default: false,
    },
    modalLayer: {
      type: Number,
      default: 1
    },
    // userId: {
    //   type: Number,
    //   default: null
    // }
  },
  data() {
    return {
      modalDataIsLoading: false,
      userData: {},
      addressModal: false,
      confirmDeleteAddressModal: false,
    }
  },
  watch: {
    user: {
      handler(value) {
        this.userData = value ? { ...value } : {}
      },
      deep: true,
      immediate: true
    },
    '$store.state.secondLayerIsOpened'() {
      if (this.modalLayer !== 1) return false
      if (!this.$store.state.secondLayerIsOpened) {
        if (this.confirmDeleteAddressModal) {
          this.closeConfirmDeleteAddressModal()
        }
        if (this.addressModal) {
          this.closeAddressModal()
        }
      }
    },
    '$store.state.thirdLayerIsOpened'() {
      if (this.modalLayer !== 2) return false
      if (!this.$store.state.thirdLayerIsOpened) {
        if (this.confirmDeleteAddressModal) {
          this.closeConfirmDeleteAddressModal()
        }
        if (this.addressModal) {
          this.closeAddressModal()
        }
      }
    },
  },
  methods: {
    closeAddressModal() {
      this.addressModal = false
      if (this.modalLayer === 2) {
        this.$store.commit('toggleThirdLayer', false)
      } else if (this.modalLayer === 1) {
        this.$store.commit('toggleSecondLayer', false)
      }
    },
    closeConfirmDeleteAddressModal() {
      this.confirmDeleteAddressModal = false
      if (this.modalLayer === 2) {
        this.$store.commit('toggleThirdLayer', false)
      } else if (this.modalLayer === 1) {
        this.$store.commit('toggleSecondLayer', false)
      }
    },
    addAddress() {
      this.userData.address = {}
      this.addressModal = true
      if (this.modalLayer === 2) {
        this.$store.commit('toggleThirdLayer', true)
      } else if (this.modalLayer === 1) {
        this.$store.commit('toggleSecondLayer', true)
      }
    },
    editAddress(userAddress) {
      this.userData.address = { ...userAddress }
      this.addressModal = true
      if (this.modalLayer === 2) {
        this.$store.commit('toggleThirdLayer', true)
      } else if (this.modalLayer === 1) {
        this.$store.commit('toggleSecondLayer', true)
      }
    },
    updateItem(newAddress, updateItems = true) {
      this.userData.address = newAddress ? { ...newAddress } : null
      this.$emit('update-item', newAddress, updateItems)
    },
    updateItems() {
      this.$emit('update-items')
    },
    deleteAddressOnClick() {
      this.confirmDeleteAddressModal = true
      if (this.modalLayer === 2) {
        this.$store.commit('toggleThirdLayer', true)
      } else if (this.modalLayer === 1) {
        this.$store.commit('toggleSecondLayer', true)
      }
    },
    async deleteAddress() {
      const addressId = this.userData?.address?.id
      this.modalDataIsLoading = true

      if (!addressId) {
        this.userData.address = null
        this.$emit('update-item', null)
        this.modalDataIsLoading = false
        this.closeConfirmDeleteAddressModal()
        return
      }

      try {
        const { status, data } = await httpClient.post(`user-address/delete`, { id: addressId })
        if (status === 200 && data?.success) {
          this.$toast.add({severity:'success', detail: this.$t('Data deleted'), life: settings.toastLife});
          this.userData.address = null
          this.$emit('update-item', null)
          this.$emit('update-items')
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.modalDataIsLoading = false
      }

      this.closeConfirmDeleteAddressModal()
    },
  },
  computed: {
    userAddressIsNotEmpty() {
      const address = this.userData?.address
      return !!address && [
        'address', 'address2', 'city', 'state', 'postcode', 'country_id', 'comment'
      ].some(field => {
        const value = address[field]
        return value != null && String(value).trim() !== ''
      })
    },
  }
}
</script>